import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import TextEditor from "../TextEditor";
import { toast } from "react-toastify";
import { convertToSlug } from "../../utils/helps";
import ApiCustom from "../../utils/ApiCustom";
import { PhotoIcon } from "@heroicons/react/24/outline";
import { getNotificationPosts } from "../../api/post";

const postSchema = Yup.object().shape({
  title: Yup.string()
    .trim()
    .min(5, "Tiêu đề phải ít nhất 5 ký tự")
    .required("Tiêu đề không được để trống"),

  content: Yup.string()
    .trim()
    .test("content-length", "Nội dung không được để trống", (value) => {
      return value && value.trim().length >= 10;
    })
    .required("Nội dung không được để trống"),

  categoryId: Yup.string().trim().required("Danh mục không được để trống"),

  parentId: Yup.string().nullable(), // không bắt buộc

  position: Yup.string()
    .oneOf(["1", "2", "3"], "Mức độ ưu tiên không hợp lệ")
    .nullable(),
});

const PostModal = ({ postId, onClose }) => {
  const [post, setPost] = useState(null);
  const [notificationPosts, setNotificationPosts] = useState([]); // danh sách post type=notification
  const [categoryOptions, setCategoryOptions] = useState([]);

  const [formData, setFormData] = useState({
    title: "",
    content: null,
    status: "draft",
    seoTitle: "",
    seoDescription: "",
    seoKeywords: "",
    cateGory: "",
    images: null,
    imagesUrl: "",
    categoryId: "",
    parentId: "", // post cha (notification)
    position: "2", // 1: cao, 2: trung bình, 3: thấp (default trung bình)
  });

  const [errors, setErrors] = useState({});
  const { categories } = useSelector((state) => state.categories);
  const [logoPreview, setLogoPreview] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (post) {
      setFormData((prev) => ({
        ...prev,
        title: post?.title || "",
        content: post?.content || null,
        status: post?.status || "draft",
        seoTitle: post?.seoTitle || "",
        seoDescription: post?.seoDescription || "",
        seoKeywords: post?.seoKeywords || "",
        cateGory: post?.cateGory || "",
        imagesUrl: post?.images?.[0]?.url || "",
        categoryId: post?.categoryId || "",
        parentId: post?.parentId ? String(post.parentId) : "",
        position: post?.position ? String(post.position) : "3",
      }));
    }
  }, [post]);

  const getPosts = useCallback(async () => {
    await ApiCustom.get(`/posts/${postId}`)
      .then((res) => {
        setPost(res.data?.data);
      })
      .catch((err) => console.log(err));
  }, [postId]);

  useEffect(() => {
    if (postId) {
      getPosts();
    }
  }, [postId, getPosts]);

  const fetchNotificationPosts = async () => {
    const data = await getNotificationPosts();
    setNotificationPosts(data?.data || []);
  };

  const fetchCategoryOptions = async () => {
    try {
      const res = await ApiCustom.get(
        "categories/public?t=post,navbar,training_recruitment,practice_certificate,legal_document,cadoor"
      );
      const data = res.data?.data?.data || [];
      setCategoryOptions(data.filter((item) => item.permissionId !== null));
    } catch (error) {
      console.log(error);
      setCategoryOptions(categories || []);
    }
  };

  useEffect(() => {
    fetchNotificationPosts();
    fetchCategoryOptions();
  }, []);

  const { status: saveStatus, error } = useSelector((state) => state.posts);

  const handleChange = async (e) => {
    const name = e.name || e.target?.name;
    const value = e.value || e.target?.value;
    const files = e.target?.files;
    let newValue = name === "images" && files ? files[0] : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    try {
      if (name === "images" && newValue instanceof File) {
        const allowedTypes = ["image/jpeg", "image/png", "image/gif"];
        if (!allowedTypes.includes(newValue.type)) {
          setErrors((prev) => ({
            ...prev,
            logo: "Chỉ chấp nhận file ảnh (JPEG, PNG, GIF)",
          }));
          return;
        }

        if (newValue.size > 10 * 1024 * 1024) {
          setErrors((prev) => ({
            ...prev,
            images: "Kích thước file không được vượt quá 10MB",
          }));
          return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoPreview(reader.result);
        };
        reader.readAsDataURL(newValue);
        return;
      }

      // validate field nếu có trong schema
      if (postSchema.fields[name]) {
        await Yup.reach(postSchema, name).validate(newValue);
        setErrors((prev) => ({
          ...prev,
          [name]: undefined,
        }));
      }
    } catch (validationError) {
      setErrors((prev) => ({
        ...prev,
        [name]: validationError.message,
      }));
    }
  };

  const handleErr = (err) => {
    const errorMessage = err.response?.data;
    if (errorMessage) {
      const messages = errorMessage?.messages || errorMessage?.message;

      if (typeof messages === "string") {
        return toast.error(messages);
      } else if (typeof messages === "object" && messages !== null) {
        Object.entries(messages).forEach(([key, value]) => {
          if (key === "slug") {
            toast.error("Tiêu đề đã tồn tại, vui lòng chọn tiêu đề khác");
            return;
          }
          if (Array.isArray(value)) {
            value.forEach((msg) => toast.error(`${key}: ${msg}`));
          } else {
            toast.error(`${key}: ${value}`);
          }
        });
        return;
      }
    }
    toast.error("Đã xảy ra lỗi!");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const finalFormData = {
        ...formData,
      };

      let validationSchema = postSchema;
      if (post?.id && !formData.images) {
        validationSchema = postSchema.omit(["images"]);
      }

      await validationSchema.validate(finalFormData, { abortEarly: false });

      const submitData = new FormData();
      Object.keys(finalFormData).forEach((key) => {
        const value = finalFormData[key];
        if (value !== null && value !== undefined && value !== "") {
          submitData.append(key, value);
        }
      });

      submitData.append("slug", convertToSlug(finalFormData?.title));

      try {
        setIsSubmitting(true);
        if (post?.id) {
          ApiCustom.put(`/posts-update/${post.id}`, submitData, {
            "Content-Type": "application/json",
          })
            .then((res) => {
              toast.success("Cập nhật bài viết thành công!");
              onClose();
            })
            .catch((err) => {
              handleErr(err);
            })
            .finally(() => setIsSubmitting(false));
        } else {
          ApiCustom.post("/blog-posts-create", submitData, {
            "Content-Type": "application/json",
          })
            .then((res) => {
              toast.success("Thêm bài viết thành công!");
              onClose();
            })
            .catch((err) => {
              handleErr(err);
            })
            .finally(() => setIsSubmitting(false));
        }
      } catch (error) {
        console.error("Error submitting post:", error);
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError && error.inner) {
        const formErrors = {};
        error.inner.forEach((err) => {
          formErrors[err.path] = err.message;
        });
        setErrors(formErrors);
      }
      setIsSubmitting(false);
    }
  };

  const isLoading = isSubmitting || saveStatus === "loading";

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-base-100 rounded-lg w-3/4 max-h-[90vh] overflow-y-auto">
        <div className="flex p-6 sticky top-0 bg-base-100 z-10 pb-4 justify-between items-center">
          <h2 className="text-xl font-semibold mb-4">
            {post ? "Chỉnh sửa" : "Thêm"} bài viết
          </h2>
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
            disabled={isLoading}
          >
            X
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6">
            {/* Tiêu đề */}
            <div className="mb-4">
              <label htmlFor="title" className="block text-sm font-medium">
                Tiêu đề<i className="text-red-600"> *</i>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={`w-full p-2 border rounded ${
                  errors.title ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1">{errors.title}</p>
              )}
            </div>

            {/* Danh mục */}
            <div className="mb-4">
              <label htmlFor="categoryId" className="block text-sm font-medium">
                Danh mục<i className="text-red-600"> *</i>
              </label>
              <select
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option key={0} value="">
                  Chọn danh mục
                </option>
                {categoryOptions.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              {errors.categoryId && (
                <p className="text-red-500 text-sm mt-1">{errors.categoryId}</p>
              )}
            </div>

            {/* parentId: chọn post thông báo */}
            <div className="mb-4">
              <label htmlFor="parentId" className="block text-sm font-medium">
                Thuộc hoạt động/sự kiện
              </label>
              <select
                name="parentId"
                value={formData.parentId}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option value="">Không chọn</option>
                {notificationPosts.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.title}
                  </option>
                ))}
              </select>
              {errors.parentId && (
                <p className="text-red-500 text-sm mt-1">{errors.parentId}</p>
              )}
            </div>

            {/* position: mức độ ưu tiên */}
            <div className="mb-4">
              <label htmlFor="position" className="block text-sm font-medium">
                Mức độ ưu tiên
              </label>
              <select
                name="position"
                value={formData.position}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option value="1">1 - Cao</option>
                <option value="2">2 - Trung bình</option>
                <option value="3">3 - Thấp</option>
              </select>
              {errors.position && (
                <p className="text-red-500 text-sm mt-1">{errors.position}</p>
              )}
            </div>

            {/* Nội dung */}
            <div className="mb-4 ">
              <label className="block text-sm font-medium mb-2">
                Nội dung<i className="text-red-600"> *</i>
              </label>
              <TextEditor
                value={formData.content}
                onChange={handleChange}
                name="content"
                importCustomInsertImage={true}
                minHeight="500px"
              />
              {errors.content && (
                <p className="text-red-500 text-sm mt-1">{errors.content}</p>
              )}
            </div>

            {/* Ảnh bìa */}
            <div className="mb-4">
              <label htmlFor="images" className="block text-sm font-medium">
                Ảnh bìa
              </label>
              <div className="flex items-center space-x-4">
                <div className="h-20 w-20 mt-2 border-2 border-gray-300 border-dashed rounded-lg overflow-hidden">
                  {logoPreview ? (
                    <img
                      src={`${
                        formData.images
                          ? logoPreview
                          : `${process.env.REACT_APP_API_URL}media/${logoPreview}`
                      }`}
                      alt="Ảnh bìa"
                      className="h-20 w-20 object-contain rounded"
                    />
                  ) : formData.imagesUrl ? (
                    <img
                      src={`${process.env.REACT_APP_API_URL}media/${formData.imagesUrl}`}
                      className="h-20 w-20 object-contain rounded"
                      alt="Ảnh bìa"
                    />
                  ) : (
                    <div className="h-20 w-20 flex items-center justify-center text-gray-400">
                      <PhotoIcon className="w-10 h-10 text-gray-300" />
                    </div>
                  )}
                </div>

                <input
                  type="file"
                  name="images"
                  accept="image/jpeg,image/png,image/gif,image/jpg"
                  onChange={handleChange}
                  className="file-input file-input-bordered w-full max-w-xs"
                />
              </div>

              {errors.images && (
                <p className="text-red-500 text-sm mt-1">{errors.images}</p>
              )}
            </div>

            {/* SEO fields */}
            <div className="mb-4">
              <label htmlFor="seoTitle" className="block text-sm font-medium">
                SEO Tiêu đề
              </label>
              <input
                type="text"
                name="seoTitle"
                value={formData.seoTitle}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            <div className="mb-4">
              <label
                htmlFor="seoDescription"
                className="block text-sm font-medium"
              >
                SEO Mô tả
              </label>
              <textarea
                name="seoDescription"
                value={formData.seoDescription}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
                rows={2}
              ></textarea>
            </div>
            <div className="mb-4">
              <label
                htmlFor="seoKeywords"
                className="block text-sm font-medium"
              >
                SEO Từ khóa
              </label>
              <input
                type="text"
                name="seoKeywords"
                value={formData.seoKeywords}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded"
                placeholder={`Cách nhau bằng dấu phẩy, tối thiểu 3 từ`}
              />
            </div>
            {formData.cateGory && (
              <div className="mb-4">
                <label
                  htmlFor="seoKeywords"
                  className="block text-sm font-medium"
                >
                  Ghi chú
                </label>
                <input
                  type="text"
                  name="seoKeywords"
                  value={formData.cateGory}
                  onChange={handleChange}
                  readOnly
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex border-t sticky bottom-0 bg-base-100 z-10 p-6 justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 btn btn-sm rounded"
              disabled={isLoading}
            >
              Hủy
            </button>
            <button
              type="button"
              onClick={() => setShowPreview(true)}
              className="px-4 py-2 btn btn-outline rounded btn-sm"
              disabled={isLoading}
            >
              Xem trước
            </button>
            <button
              type="submit"
              className={`px-4 py-2 btn btn-primary rounded btn-sm ${
                isLoading ? "loading" : ""
              }`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="loading loading-spinner loading-xs mr-2"></span>
                  Đang lưu...
                </>
              ) : (
                "Lưu"
              )}
            </button>
          </div>

          {error && (
            <p className="text-red-500 text-sm mt-2">
              Đã xảy ra lỗi: {error.message}
            </p>
          )}

          {showPreview && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
              <div className="bg-base-100 w-[800px] max-w-[95vw] max-h-[90vh] overflow-y-auto rounded-lg shadow-xl">
                <div className="flex items-center justify-between p-4 border-b">
                  <h3 className="text-lg font-semibold">Xem trước</h3>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="btn btn-sm"
                  >
                    ✕
                  </button>
                </div>

                <div className="p-6 space-y-4">
                  <h1 className="text-3xl font-bold">
                    {formData.title?.trim() || "(Chưa có tiêu đề)"}
                  </h1>
                  <div
                    className="prose max-w-none break-words rich-content"
                    dangerouslySetInnerHTML={{
                      __html: formData?.content,
                    }}
                  />
                </div>

                <div className="p-4 border-t flex justify-end">
                  <button
                    className="btn btn-sm"
                    onClick={() => setShowPreview(false)}
                  >
                    Đóng
                  </button>
                </div>
              </div>
              <style>{`
                .rich-content, .rich-content * { box-sizing: border-box; }

                .rich-content p:not([style*="text-align"]),
                .rich-content li:not([style*="text-align"]),
                .rich-content blockquote:not([style*="text-align"]),
                .rich-content div:not([style*="text-align"]) {
                  text-align: justify !important;
                }

                .rich-content img,
                .rich-content video,
                .rich-content svg,
                .rich-content canvas {
                  max-width: 100% !important;
                  height: auto !important;
                  display: block;
                  margin-left: auto;
                  margin-right: auto;
                }
                .rich-content iframe {
                  width: 100% !important;
                  max-width: 100% !important;
                }
                .rich-content table {
                  display: block;
                  width: 100% !important;
                  max-width: 100% !important;
                  overflow-x: auto;
                  -webkit-overflow-scrolling: touch;
                  border-collapse: collapse;
                }
                .rich-content pre {
                  max-width: 100%;
                  overflow-x: auto;
                  white-space: pre-wrap;
                  word-break: break-word;
                }
                .rich-content a { word-break: break-word; }
                .rich-content figure { margin: 0; }
                .rich-content [style*="width"] { max-width: 100% !important; }
              `}</style>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default PostModal;